import { useEffect, useRef, useState } from 'react'
import { Copy, MoveHorizontal, PaintBucket, Pencil, SlidersHorizontal, Trash2 } from 'lucide-react'
import type { Chart } from 'klinecharts'
import { ColorPopover, rgba } from './WorkbenchColorPicker'
import {
  BitsPopover,
  CheckRow,
  EXTEND_LABEL,
  Grip,
  STYLES,
  ToolbarButton,
  TREND_NAMES,
  WIDTHS,
  modeFromName,
  type DragOffset,
} from './WorkbenchFloatingBits'
import { FibLevelsPopover, type FibConfigPatch } from './WorkbenchFibLevels'
import { DEFAULT_FIB_LEVELS } from './customOverlays'
import { LEG_MARKERS } from './legTypes'
import type { SelectedDrawing } from './types'

// Floating style toolbar — the "soul of the TV feel". Appears above a selected
// drawing; restyles it live (outline / fill / width / line-style), extends a
// trend line, or deletes it, via klinecharts overrideOverlay. Drag the left
// grip to move it.

const DEFAULT_DRAW_COLOR = '#ffd60a' // yellow default (reads better on black)

type PopoverId = 'color' | 'fill' | 'width' | 'style' | 'extend' | 'levels' | null

/** Parse an rgba() string (or plain hex) into a hex + opacity% pair. */
function parseColor(c: unknown, fallbackOpacity = 100): { hex: string; op: number } {
  if (typeof c === 'string' && c.startsWith('rgba')) {
    const m = c.match(/[\d.]+/g) ?? []
    const hex =
      '#' +
      [m[0], m[1], m[2]]
        .map((n) => Math.round(Number(n)).toString(16).padStart(2, '0'))
        .join('')
    return { hex, op: Math.round((Number(m[3]) || 1) * 100) }
  }
  if (typeof c === 'string' && c.length > 0) return { hex: c, op: fallbackOpacity }
  return { hex: DEFAULT_DRAW_COLOR, op: fallbackOpacity }
}

export interface WorkbenchFloatingToolbarProps {
  chart: Chart
  selected: SelectedDrawing
  onExtend: (mode: string) => void
  onFibConfig: (patch: FibConfigPatch) => void
  onDelete: (id: string) => void
  onClone: (id: string) => void
  onMutated: () => void
  onEditLeg: (id: string) => void
}

export function WorkbenchFloatingToolbar({
  chart,
  selected,
  onExtend,
  onFibConfig,
  onDelete,
  onClone,
  onMutated,
  onEditLeg,
}: WorkbenchFloatingToolbarProps) {
  const [openPopover, setOpenPopover] = useState<PopoverId>(null)
  const [drag, setDrag] = useState<DragOffset>({ dx: 0, dy: 0 })
  // Bumped after each in-place restyle so the render below re-reads the
  // overlay's live styles (current colour / width / line-style).
  const [nonce, setNonce] = useState(0)
  const rootRef = useRef<HTMLDivElement | null>(null)

  // Reset on a *different* drawing — keyed on selected.key (stable across an
  // extend swap, which changes id but not key), so the Extend dropdown stays
  // open until an outside click. Render-time state adjustment (no effect).
  const [prevKey, setPrevKey] = useState(selected.key)
  if (prevKey !== selected.key) {
    setPrevKey(selected.key)
    setOpenPopover(null)
    setDrag({ dx: 0, dy: 0 })
  }

  useEffect(() => {
    if (!openPopover) return
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpenPopover(null)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [openPopover])

  const isRect = selected.name === 'rectangle'
  const isFib = selected.name === 'fibRetracement'
  const showExtend = TREND_NAMES.includes(selected.name) || isFib // both expose extend left/right

  // Current colour / width / style come straight off the overlay's live styles
  // (nonce forces a re-read after each restyle).
  void nonce
  const overlay = chart.getOverlayById(selected.id)
  const st = overlay?.styles
  const ed = (overlay?.extendData ?? {}) as { lineSize?: number }
  const lineRaw = isRect ? st?.polygon?.borderColor : st?.line?.color
  const rectFillRaw = st?.polygon?.color
  const fillRaw = isFib ? selected.fillColor : typeof rectFillRaw === 'string' ? rectFillRaw : undefined
  const { hex: curColor, op: curOpacity } = parseColor(lineRaw)
  const { hex: curFill, op: curFillOpacity } = parseColor(fillRaw, 10)
  const curWidth = isFib ? ed.lineSize ?? 1 : (isRect ? st?.polygon?.borderSize : st?.line?.size) ?? 1
  const curStyle: string = (isRect ? st?.polygon?.borderStyle : st?.line?.style) ?? 'solid'

  // Fib swatches reflect the level colours: a multi-colour blend when the levels
  // differ (TV's "mixed" indicator), a solid swatch when they're all one colour.
  const fibLevels = selected.levels ?? DEFAULT_FIB_LEVELS
  const fibColors = fibLevels.filter((l) => l.enabled !== false).map((l) => l.color)
  const fibMixed = fibColors.length > 1 && !fibColors.every((c) => c === fibColors[0])
  const fibSwatch = (fallback: string) =>
    fibMixed ? `linear-gradient(90deg, ${fibColors.join(', ')})` : fibColors[0] ?? fallback
  const borderSwatch = isFib ? fibSwatch(curColor) : curColor
  const fillSwatch = isFib ? selected.fillColor ?? fibSwatch(curFill) : curFill

  // Route style patches to the right klinecharts style group per overlay type:
  // rectangles use `polygon` (border + fill), lines use `line`. The pencil sets
  // the outline; the paint-bucket (rect + fib) sets the fill.
  const restyle = (patch: Record<string, unknown>) => {
    chart.overrideOverlay({ id: selected.id, styles: { [isRect ? 'polygon' : 'line']: patch } })
    onMutated()
    setNonce((n) => n + 1)
  }
  // Colour/fill/width on a fib apply to ALL levels at once (per-level lives in
  // the Levels dropdown); fib changes flow through onFibConfig (which records
  // its own undo step). Other drawings restyle in place and signal onMutated.
  const applyColour = (hex: string, op: number) => {
    if (isFib) {
      onFibConfig({ levels: fibLevels.map((l) => ({ ...l, color: rgba(hex, op) })) })
      return
    }
    restyle({ [isRect ? 'borderColor' : 'color']: rgba(hex, op) })
  }
  const applyFill = (hex: string, op: number) => {
    if (isFib) {
      onFibConfig({ fillColor: rgba(hex, op) })
      return
    }
    chart.overrideOverlay({ id: selected.id, styles: { polygon: { color: rgba(hex, op) } } })
    onMutated()
    setNonce((n) => n + 1)
  }
  const applyWidth = (w: number) => {
    if (isFib) {
      onFibConfig({ lineSize: w })
      return
    }
    restyle({ [isRect ? 'borderSize' : 'size']: w })
  }

  const mode = modeFromName(selected.name, selected.extend)
  const leftOn = mode === 'left' || mode === 'both'
  const rightOn = mode === 'right' || mode === 'both'
  const setExtend = (l: boolean, r: boolean) => onExtend(l && r ? 'both' : l ? 'left' : r ? 'right' : 'none')

  const left = Math.max(96, Math.min(selected.x + drag.dx, selected.paneW - 96))
  const top = Math.max(40, selected.y - 16 + drag.dy)
  const grip = <Grip drag={drag} setDrag={setDrag} />

  // Strategy markers: no style controls — just edit the note, clone, or delete.
  // The note / leg / colour live in extendData and travel with a clone.
  if (LEG_MARKERS.includes(selected.name)) {
    // Extra clearance: markers carry a label pill above the point, so the toolbar
    // needs more gap than the shared offset gives — lift it further up.
    const legTop = Math.max(40, top - 26)
    return (
      <div ref={rootRef} className="absolute z-40" style={{ left, top: legTop, transform: 'translate(-50%, -100%)' }}>
        <div className="relative flex items-center gap-0.5 rounded-md border border-border bg-popover px-1 py-1 shadow-popover">
          {grip}
          <ToolbarButton title="Edit note" onClick={() => onEditLeg(selected.id)}>
            <Pencil size={15} strokeWidth={1.75} />
          </ToolbarButton>
          <ToolbarButton title="Clone" onClick={() => onClone(selected.id)}>
            <Copy size={15} strokeWidth={1.75} />
          </ToolbarButton>
          <button
            type="button"
            title="Delete"
            aria-label="Delete"
            onClick={() => onDelete(selected.id)}
            className="inline-flex h-8 min-w-7 items-center justify-center rounded-md px-1.5 transition-colors cursor-pointer text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 size={15} strokeWidth={1.75} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div ref={rootRef} className="absolute z-40" style={{ left, top, transform: 'translate(-50%, -100%)' }}>
      <div className="relative flex items-center gap-0.5 rounded-md border border-border bg-popover px-1 py-1 shadow-popover">
        {/* drag grip (2 cols × 3 rows) */}
        {grip}

        {/* Each control wraps its own dropdown so the popover opens directly under
            its button (not anchored to the whole pill). */}

        {/* levels (fib only) — first control: toggle/recolour each fib level */}
        {isFib && (
          <div className="relative">
            <ToolbarButton
              active={openPopover === 'levels'}
              title="Levels"
              onClick={() => setOpenPopover(openPopover === 'levels' ? null : 'levels')}
            >
              <span className="flex items-center gap-1.5">
                <SlidersHorizontal size={14} strokeWidth={1.75} />
                <span className="text-caption">Levels</span>
              </span>
            </ToolbarButton>
            {openPopover === 'levels' && (
              <BitsPopover>
                <FibLevelsPopover levels={fibLevels} labelMode={selected.labelMode} onChange={onFibConfig} />
              </BitsPopover>
            )}
          </div>
        )}

        {/* colour: pencil + swatch. Rectangle/fib = outline; on fib recolours all levels. */}
        <div className="relative">
          <ToolbarButton
            active={openPopover === 'color'}
            title={isRect || isFib ? 'Border colour' : 'Colour'}
            onClick={() => setOpenPopover(openPopover === 'color' ? null : 'color')}
          >
            <span className="flex flex-col items-center gap-0.5">
              <Pencil size={14} strokeWidth={1.75} />
              <span className="block h-[3px] w-4 rounded-sm" style={{ background: borderSwatch }} />
            </span>
          </ToolbarButton>
          {openPopover === 'color' && (
            <BitsPopover>
              <ColorPopover
                curColor={curColor}
                curOpacity={curOpacity}
                onColor={(c) => applyColour(c, curOpacity)}
                onOpacity={(v) => applyColour(curColor, v)}
              />
            </BitsPopover>
          )}
        </div>

        {/* fill: paint bucket + swatch (rectangles + fib), like TV */}
        {(isRect || isFib) && (
          <div className="relative">
            <ToolbarButton
              active={openPopover === 'fill'}
              title="Fill colour"
              onClick={() => setOpenPopover(openPopover === 'fill' ? null : 'fill')}
            >
              <span className="flex flex-col items-center gap-0.5">
                <PaintBucket size={14} strokeWidth={1.75} />
                <span className="block h-[3px] w-4 rounded-sm" style={{ background: fillSwatch }} />
              </span>
            </ToolbarButton>
            {openPopover === 'fill' && (
              <BitsPopover>
                <ColorPopover
                  curColor={curFill}
                  curOpacity={curFillOpacity}
                  onColor={(c) => applyFill(c, curFillOpacity)}
                  onOpacity={(v) => applyFill(curFill, v)}
                />
              </BitsPopover>
            )}
          </div>
        )}

        {/* width — applies to all level lines on a fib */}
        <div className="relative">
          <ToolbarButton
            active={openPopover === 'width'}
            title="Line width"
            onClick={() => setOpenPopover(openPopover === 'width' ? null : 'width')}
          >
            <span className="flex items-center gap-1.5">
              <span className="block w-4 bg-foreground" style={{ height: curWidth }} />
              <span className="text-caption tabular-nums">{curWidth}px</span>
            </span>
          </ToolbarButton>
          {openPopover === 'width' && (
            <BitsPopover>
              {WIDTHS.map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => {
                    applyWidth(w)
                    setOpenPopover(null)
                  }}
                  className="w-full flex items-center gap-3 px-2 py-1.5 rounded-md text-left text-caption text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors cursor-pointer"
                >
                  <span className="inline-block w-9 bg-foreground" style={{ height: w }} />
                  {w}px
                </button>
              ))}
            </BitsPopover>
          )}
        </div>

        {/* line style (dashed/solid) — not shown for fib */}
        {!isFib && (
          <div className="relative">
            <ToolbarButton
              active={openPopover === 'style'}
              title="Line style"
              onClick={() => setOpenPopover(openPopover === 'style' ? null : 'style')}
            >
              <span className="flex items-center gap-1.5">
                <span
                  className="block w-4 border-t-2 border-foreground"
                  style={{ borderStyle: curStyle === 'dashed' ? 'dashed' : 'solid' }}
                />
                <span className="text-caption">{curStyle === 'dashed' ? 'Dashed' : 'Solid'}</span>
              </span>
            </ToolbarButton>
            {openPopover === 'style' && (
              <BitsPopover>
                {STYLES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      restyle(isRect ? { borderStyle: s.id } : { style: s.id })
                      setOpenPopover(null)
                    }}
                    className="w-full flex items-center gap-3 px-2 py-1.5 rounded-md text-left text-caption text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors cursor-pointer"
                  >
                    <span
                      className="inline-block w-9 border-t-2 border-foreground"
                      style={{ borderStyle: s.id === 'dashed' ? 'dashed' : 'solid' }}
                    />
                    {s.label}
                  </button>
                ))}
              </BitsPopover>
            )}
          </div>
        )}

        {/* extend (trend lines + fib) — right-aligned dropdown (button sits near the right) */}
        {showExtend && (
          <div className="relative">
            <ToolbarButton
              active={openPopover === 'extend'}
              title="Extend"
              onClick={() => setOpenPopover(openPopover === 'extend' ? null : 'extend')}
            >
              <span className="flex items-center gap-1.5">
                <MoveHorizontal size={14} strokeWidth={1.75} />
                <span className="text-caption whitespace-nowrap">{EXTEND_LABEL[mode]}</span>
              </span>
            </ToolbarButton>
            {openPopover === 'extend' && (
              <BitsPopover align="right">
                {/* TV's exact model: two toggles. Stays open on toggle. */}
                <CheckRow label="Extend left line" checked={leftOn} onToggle={() => setExtend(!leftOn, rightOn)} />
                <CheckRow label="Extend right line" checked={rightOn} onToggle={() => setExtend(leftOn, !rightOn)} />
              </BitsPopover>
            )}
          </div>
        )}

        <div className="mx-0.5 h-5 w-px bg-border" />
        <ToolbarButton title="Clone" onClick={() => onClone(selected.id)}>
          <Copy size={15} strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarButton title="Delete" onClick={() => onDelete(selected.id)}>
          <Trash2 size={15} strokeWidth={1.75} />
        </ToolbarButton>
      </div>
    </div>
  )
}
